import { useEffect, useState } from "react";
import http from "./httpClient";
import { getLocalValue, LoacalVariables } from "./CommonFunctions";
import { triggerVendorOrdersRefresh } from "./vendorOrdersHub";
import { getFurthestStatus } from "../FrontEnd/Pharmacy/PharmacyOrder/orderTimeline";

// Tracks which orders arrived while the vendor was using the app. A new order stays "new" (its
// card blinks red) until the vendor accepts it. The siren sounds when a new order arrives and stops
// as soon as the vendor accepts any order; it sounds again only when another new order arrives.
//
// - When the app opens, the orders that already exist are recorded as "seen" (not new).
// - After that, an order id that wasn't seen before is new, if it is an order the vendor still has
//   to deal with, and stays new until acceptNewOrder() is called for it, or until a fresh order
//   list shows it no longer needs the vendor (cancelled, assigned elsewhere, removed).
// - The state lives in sessionStorage, so a page refresh keeps it, and it is kept per user.

interface Tracker {
  seen: Set<string>;
  highlighted: Set<string>;
  // New orders the siren is sounding for; cleared when the vendor accepts an order
  alerting: Set<string>;
  // Orders that arrived while the app was open, oldest first (order lists show the latest first)
  arrived: string[];
}

const KEY_PREFIX = "vendorNewOrders:";
const MAX_SEEN_IDS = 2000;
const MAX_ARRIVED_IDS = 500;

let tracker: Tracker | null = null;
let trackerKey = "";
const subscribers = new Set<() => void>();

const normalize = (value: any) => String(value || "").trim().toLowerCase();

// Lab: an order is waiting for the vendor until a technician is assigned or it is finished
const LAB_NOT_WAITING_STATUSES = [
  "assigned", "sample collected", "samplecollected", "in progress", "inprogress", "inprocess",
  "completed", "delivered", "fulfilled", "report ready", "reportready",
  "report generated", "reportgenerated", "report uploaded", "reportuploaded", "report submitted", "reportsubmitted",
  "cancelled", "canceled", "rejected", "refund", "refunded", "refund initiated", "refundinitiated",
];

export const isLabOrderWaiting = (order: { status?: string; paymentStatus?: string }) =>
  !LAB_NOT_WAITING_STATUSES.includes(normalize(order.status)) &&
  !normalize(order.paymentStatus).startsWith("refund");

// Pharmacy: finished orders live in Order History
const PHARMACY_CLOSED_STATUSES = [
  "delivered", "completed", "fulfilled",
  "cancelled", "canceled", "rejected", "refund", "refunded", "refund initiated", "refundinitiated",
];

export const isPharmacyStatusClosed = (status?: string) => PHARMACY_CLOSED_STATUSES.includes(normalize(status));

const currentKey = () => `${KEY_PREFIX}${getLocalValue(LoacalVariables.UserId) || ""}`;

const loadTracker = (): Tracker | null => {
  const key = currentKey();
  if (tracker && trackerKey === key) return tracker;
  trackerKey = key;
  tracker = null;
  try {
    const parsed = JSON.parse(sessionStorage.getItem(key) || "null");
    if (parsed && Array.isArray(parsed.seen)) {
      const highlighted: string[] = Array.isArray(parsed.highlighted) ? parsed.highlighted : [];
      tracker = {
        seen: new Set<string>(parsed.seen),
        highlighted: new Set<string>(highlighted),
        alerting: new Set<string>(Array.isArray(parsed.alerting) ? parsed.alerting : highlighted),
        arrived: Array.isArray(parsed.arrived) ? parsed.arrived : [],
      };
    }
  } catch {
    // ignore unreadable data
  }
  return tracker;
};

const saveTracker = () => {
  if (tracker) {
    try {
      sessionStorage.setItem(
        trackerKey,
        JSON.stringify({
          seen: Array.from(tracker.seen).slice(-MAX_SEEN_IDS),
          highlighted: Array.from(tracker.highlighted),
          alerting: Array.from(tracker.alerting),
          arrived: tracker.arrived.slice(-MAX_ARRIVED_IDS),
        })
      );
    } catch {
      // storage unavailable: new orders just won't stay marked after a refresh
    }
  }
  subscribers.forEach((notify) => notify());
};

export interface OrderEntry {
  id: string;
  // Whether this order should be marked new if it has just arrived
  needsAttention: boolean;
}

// Call with the orders from a fresh load of an order list. Returns how many were newly marked.
// "reliable" means the request succeeded; only a reliable list may start the tracking, because
// a failed request must not make every existing order look new later.
export const ingestOrders = (entries: OrderEntry[], reliable: boolean): number => {
  if (!getLocalValue(LoacalVariables.UserId)) return 0;
  const existing = loadTracker();
  if (!existing) {
    if (!reliable) return 0;
    tracker = {
      seen: new Set(entries.map((e) => e.id).filter(Boolean)),
      highlighted: new Set(),
      alerting: new Set(),
      arrived: [],
    };
    saveTracker();
    return 0;
  }
  let marked = 0;
  let changed = false;
  entries.forEach(({ id, needsAttention }) => {
    if (id && !existing.seen.has(id)) {
      existing.seen.add(id);
      existing.arrived.push(id);
      changed = true;
      if (needsAttention) {
        existing.highlighted.add(id);
        existing.alerting.add(id);
        marked += 1;
      }
    }
  });
  // A complete list also tells which new orders no longer need the vendor (cancelled, assigned
  // elsewhere, removed). Those have no Accept button any more, so they must not keep the siren on.
  if (reliable) {
    const waiting = new Set(entries.filter((e) => e.needsAttention).map((e) => e.id));
    existing.highlighted.forEach((id) => {
      if (!waiting.has(id)) {
        existing.highlighted.delete(id);
        existing.alerting.delete(id);
        changed = true;
      }
    });
  }
  if (changed) saveTracker();
  return marked;
};

// The vendor accepted the order: stops its blinking and silences the siren. Other new orders not
// accepted yet keep blinking; the siren sounds again when another new order arrives.
export const acceptNewOrder = (id: string) => {
  const existing = loadTracker();
  if (!existing) return;
  const wasNew = existing.highlighted.delete(id);
  const wasAlerting = existing.alerting.size > 0;
  existing.alerting.clear();
  if (wasNew || wasAlerting) saveTracker();
};

let syncing = false;

// Loads the vendor's orders and marks the ones not seen before as new. Works from any page, so a
// new order is noticed even while the vendor is on a page that doesn't list orders. The first
// call after the app opens only records what already exists.
export const syncOrderTracking = async () => {
  const userId = getLocalValue(LoacalVariables.UserId);
  if (!userId || syncing) return;
  syncing = true;
  try {
    const entries: OrderEntry[] = [];
    let reliable = false;
    if (getLocalValue(LoacalVariables.UserType) === "pharmacy") {
      const [products, prescriptions]: any[] = await Promise.all([
        http.get(`pharmacy/ph_order_/ByVendor/${userId}`),
        http.get(`pharmacy/PrescriptionOrder/ByVendor/${userId}`),
      ]);
      reliable = Array.isArray(products?.data) && Array.isArray(prescriptions?.data);
      // Same ids and statuses the pharmacy order list uses
      (products?.data || []).forEach((o: any) =>
        entries.push({ id: o.orderId, needsAttention: !isPharmacyStatusClosed(o.status || "Pending") })
      );
      (prescriptions?.data || []).forEach((o: any) =>
        entries.push({
          id: o.id || o.orderId,
          // Same status the Orders page shows: the one furthest along
          needsAttention: !isPharmacyStatusClosed(
            getFurthestStatus(o.status, o.timeline?.status, o.deliveryDetails?.deliveryStatus, o.deliveryStatus) || "Pending"
          ),
        })
      );
    } else {
      const bookings: any = await http.get(`lab/LabBooking/lab/${userId}`);
      reliable = Array.isArray(bookings?.data);
      (bookings?.data || []).forEach((o: any) =>
        entries.push({ id: o.bookingId, needsAttention: isLabOrderWaiting(o) })
      );
    }
    // New orders: make any order list that is open load them too
    if (ingestOrders(entries, reliable) > 0) triggerVendorOrdersRefresh();
  } catch {
    // try again on the next check
  } finally {
    syncing = false;
  }
};

export const useNewOrderHighlights = () => {
  const [version, rerender] = useState(0);
  useEffect(() => {
    const notify = () => rerender((n) => n + 1);
    subscribers.add(notify);
    return () => {
      subscribers.delete(notify);
    };
  }, []);

  return {
    isNew: (id: string) => !!loadTracker()?.highlighted.has(id),
    // Higher for orders that arrived later while the app was open; -1 for orders that were already there
    arrivalRank: (id: string) => loadTracker()?.arrived.indexOf(id) ?? -1,
    accept: acceptNewOrder,
    newOrderCount: loadTracker()?.highlighted.size || 0,
    // New orders the siren is sounding for
    alertCount: loadTracker()?.alerting.size || 0,
    // Changes whenever the new-order set changes, for use in dependency lists
    version,
  };
};
